/*#
# svg-texture

Renders SVG content to a Babylon.js texture via an offscreen canvas.

## Modes

- **Static** (`url`): fetches SVG from a URL and renders once.
- **Dynamic** (`element`): reads a live SVG element on an interval
  (default 30 ms), capturing changes from tosijs bindings or any other
  DOM mutations. Great for HUDs, radar displays, instrument panels.

## Example — SVG on a cube

```js
import { b3d, b3dLight, SvgTexture } from 'tosijs-3d'
import { orbitCam } from 'tosijs-3d/demo-utils'

const scene = b3d(
  {
    sceneCreated(el, BABYLON) {
      orbitCam(el, { alpha: -Math.PI / 2, beta: Math.PI / 3, radius: 5, target: [0, 0, 0] })

      const box = BABYLON.MeshBuilder.CreateBox('box', { size: 1.5 }, el.scene)
      box.position.y = 0

      const tex = new SvgTexture({
        scene: el.scene,
        url: '/tosi-test-pattern.svg',
        resolution: 512,
      })

      const mat = new BABYLON.StandardMaterial('svg-mat', el.scene)
      mat.diffuseTexture = tex.texture
      box.material = mat

      el.scene.registerBeforeRender(() => {
        box.rotation.y += 0.005
      })
    },
  },
  b3dLight({ intensity: 1 }),
)

preview.append(scene)
```

Call `tex.dispose()` when done to stop the interval and release GPU memory.

## ⚠️ Web fonts do NOT survive rasterisation

A face the *document* has loaded is not available here. The SVG is serialised
to a standalone `Image`, which the browser treats as its own document: it does
not inherit the page's font faces any more than it inherits the page's CSS
custom properties (the reason [w3d-theme](?w3d-theme.ts) bakes literals). The
symptom is an in-scene panel in the fallback family while the same flat panel
renders correctly.

So a `fontFamily` in a theme reaches an in-scene panel only if the family is
installed on the device, or you embed it with `registerSvgFont` (below). The
shipped font list is generic families plus faces that ship with macOS and
Windows, which work in both presentations without it.

## Embedding a web font

`registerSvgFont(family, url, { weight, style })` fetches a font file, base64s
it, and keeps an `@font-face` rule that travels *inside* every serialised SVG
that mentions the family. Await it before the panels draw: one rasterised
before the bytes arrive renders the fallback.

```javascript
await registerSvgFont('Rosario', '/fonts/rosario.woff2')
setW3dTheme({ fontFamily: 'Rosario, serif' })
```

The bytes are inlined rather than referenced by `url()` because the serialised
document has no base URL to follow one from. That makes the SVG self-contained,
and it has a cost: a woff2 is typically 20–100 KB and base64 adds a third, so
register a family once (`fontFaceCss` caches per URL), and only the faces a
panel's markup mentions are injected into it. Pass `weight` and `style` for a
bold or italic file, or the browser synthesises a bold from the bold and you get
double emboldening. `unregisterSvgFont(family)` forgets one.
*/
/*{ "parent": "ui-in-scene", "order": 30 }*/

import * as BABYLON from '@babylonjs/core'
import { svgFontStyle } from './embed-font.js'
import { realNow } from './clock.js'

export type SvgTextureOptions = {
  /** The Babylon scene that owns this texture. */
  scene: BABYLON.Scene
  /** Texture resolution in pixels (square). Default 512. */
  resolution?: number
  /** Fetch SVG from this URL and render once. */
  url?: string
  /** Live SVG element reference (dynamic mode). */
  element?: SVGSVGElement
  /** Re-render interval in ms for dynamic mode. Default 30. */
  updateInterval?: number
  /**
   * Mipmapped, trilinear and anisotropic (default `true`). A panel is nearly
   * always drawn SMALLER than its texture: a 1024 px panel 30 cm wide at
   * 1.4 m covers about 300 px of a Quest 3's view, so unfiltered its text and
   * hairlines alias and crawl as the head moves. Costs a third more texture
   * memory and a mip rebuild per repaint. `false` for a texture always seen at
   * about one texel per pixel.
   */
  mipmaps?: boolean
  /**
   * Upload with colour multiplied by alpha; draw it with
   * `ALPHA_PREMULTIPLIED`. Straight alpha fringes dark wherever drawn pixels
   * meet transparent ones inside the texture (a panel's rounded corners: the
   * filter mixes in the transparent pixels' black). Premultiplied, those
   * pixels contribute nothing. Off by default: a light's gel wants straight.
   */
  premultiplied?: boolean
}

/**
 * Rasterize an SVG element onto a canvas context via Blob URL.
 * Reuses the provided Image instance to avoid per-frame allocation.
 */
function rasterizeSvg(
  xml: string,
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  img: HTMLImageElement,
  callback: (ok: boolean) => void
): void {
  const blob = new Blob([xml], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  img.onload = () => {
    const began = realNow()
    ctx.save()
    ctx.clearRect(0, 0, w, h)
    ctx.translate(0, h)
    ctx.scale(1, -1)
    ctx.drawImage(img, 0, 0, w, h)
    ctx.restore()
    svgTextureCost.ms += realNow() - began
    URL.revokeObjectURL(url)
    callback(true)
  }
  img.onerror = () => {
    // The callback MUST still fire: the caller holds a busy latch, and
    // swallowing the error here left it set forever — one bad rasterize froze
    // the texture for the rest of the session while the sim underneath kept
    // working (on device: "the 3D keyboard types but the plane never shows it").
    URL.revokeObjectURL(url)
    callback(false)
  }
  img.src = url
}

/**
 * What repainting SVG textures has cost on the main thread, across every
 * instance: `checks` are serialise-and-compare passes, `repaints` the ones
 * that changed and were redrawn, `ms` the time both took here. The browser's
 * own SVG rasterising happens off this thread and is NOT in it. Perf Stats
 * reads this once a second.
 */
export const svgTextureCost = { checks: 0, repaints: 0, ms: 0 }

export class SvgTexture {
  texture: BABYLON.DynamicTexture | BABYLON.Texture
  private _resolution: number
  private _interval = 0
  private _element: SVGSVGElement | undefined
  private _scene: BABYLON.Scene
  private _rendering = false
  /** A new element arrived mid-render: render again when that one lands. */
  private _again = false
  private _img = new Image()
  private _lastXml = ''
  private _warnedFailure = false
  private _premul = false

  constructor(options: SvgTextureOptions) {
    const {
      scene,
      resolution = 512,
      url,
      element,
      updateInterval = 30,
      mipmaps = true,
      premultiplied = false,
    } = options
    this._premul = premultiplied

    this._resolution = resolution
    this._element = element
    this._scene = scene

    if (url) {
      this.texture = new BABYLON.Texture(url, scene)
      this.texture.hasAlpha = true
    } else if (element) {
      const dt = new BABYLON.DynamicTexture(
        'svg-dt',
        resolution,
        scene,
        mipmaps,
        mipmaps
          ? BABYLON.Texture.TRILINEAR_SAMPLINGMODE
          : BABYLON.Texture.BILINEAR_SAMPLINGMODE
      )
      if (mipmaps) dt.anisotropicFilteringLevel = 4
      dt.hasAlpha = true
      this.texture = dt
      this.render()
      this._interval = window.setInterval(() => {
        this.render()
      }, updateInterval)
    } else {
      this.texture = new BABYLON.Texture(null, scene)
      this.texture.hasAlpha = true
    }
  }

  /**
   * Draw a different live element from now on. The texture keeps its current
   * picture until the new one is drawn (a render is asynchronous), so a panel
   * swapping its content never shows a blank frame.
   */
  setElement(element: SVGSVGElement): void {
    if (element === this._element) return
    this._element = element
    // A render in flight is of the old element: draw the new one after it.
    if (this._rendering) this._again = true
    else this.render()
  }

  /** Manually trigger a re-render from the live SVG element. */
  render(): void {
    if (!this._element || this._rendering) return
    const dt = this.texture as BABYLON.DynamicTexture
    if (!dt?.getContext) return
    const began = realNow()
    svgTextureCost.checks++
    const el = this._element.cloneNode(true) as SVGSVGElement
    el.removeAttribute('style')
    /*
    PRESENTATION-ONLY NODES.

    One UI drawn twice still has parts that belong to one side. Popup chrome is
    the case that forced this: the move and close glyphs are handled by PICKING
    (uv -> viewBox -> chromeHit), which exists only in the scene, so in the DOM
    they were painted and inert — Tonio: "the move and close affordances are
    still rendering in the DOM".

    Tonio's suggestion, and the right shape: mark a node with the presentation it
    belongs to. Here we strip anything marked `dom`; a stylesheet hides anything
    marked `texture` on the flat side. It costs one query on a clone we were
    making anyway.

    Note what this deliberately does NOT allow: differing in what you DO. This
    hides and shows nodes only. Branching behaviour between presentations is how
    the two drift apart, which is the whole warning in UI-DESIGN-NOTES → "One
    UI, two presentations".
    */
    for (const n of el.querySelectorAll('[data-presentation="dom"]')) {
      n.remove()
    }
    let xml = new XMLSerializer().serializeToString(el)
    /*
    Inline any registered web font.

    The serialised copy is its own document: it inherits no font faces from the
    page, so a `font-family` naming a web font falls back silently — the flat
    panel renders it and the textured one does not. `svgFontStyle` returns an
    `@font-face` with the bytes base64'd in, and only for families this markup
    actually mentions.

    Injected AFTER serialisation rather than into the live element, so the DOM
    panel is untouched and only the texture pays.
    */
    const fontStyle = svgFontStyle(xml)
    if (fontStyle !== '') {
      xml = xml.replace(/(<svg[^>]*>)/, `$1${fontStyle}`)
    }
    // Skip the expensive rasterize + GPU upload when the SVG is unchanged. A
    // mostly-static panel settles after one render and then costs nothing;
    // genuinely animated SVGs (e.g. a radar) still update every cycle. This is
    // the main fix for the in-XR perf creep, where a static panel was paying
    // for a full re-rasterize + texImage2D upload on every interval.
    if (xml === this._lastXml) {
      svgTextureCost.ms += realNow() - began
      return
    }
    this._rendering = true
    // DynamicTexture types this as Babylon's abstract ICanvasRenderingContext,
    // but in browsers it's the real CanvasRenderingContext2D — rasterizeSvg
    // needs the full surface API (drawImage, save, restore).
    const ctx = dt.getContext() as unknown as CanvasRenderingContext2D
    const res = this._resolution
    rasterizeSvg(xml, ctx, res, res, this._img, (ok) => {
      // Release the latch on BOTH outcomes, and commit `_lastXml` only on
      // success — so a failed frame is retried on the next tick instead of
      // being remembered as done.
      this._rendering = false
      if (this._again) {
        this._again = false
        queueMicrotask(() => this.render())
      }
      if (ok) {
        this._lastXml = xml
        const upload = realNow()
        dt.update(false, this._premul)
        svgTextureCost.repaints++
        svgTextureCost.ms += realNow() - upload
      } else if (!this._warnedFailure) {
        // Once per instance: self-healing made failure SILENT, and a
        // permanently-bad SVG (malformed markup, CSP-blocked blob) becomes an
        // invisible retry loop with a frozen texture and no clue why.
        this._warnedFailure = true
        console.warn(
          'SvgTexture: rasterize failed (will retry each tick). ' +
            'The SVG may be malformed or blob: URLs blocked by CSP.'
        )
      }
    })
    svgTextureCost.ms += realNow() - began
  }

  /** Render an arbitrary SVG string to the texture. */
  renderString(svgString: string): void {
    const dt = this.texture as BABYLON.DynamicTexture
    if (!dt?.getContext) return
    // DynamicTexture types this as Babylon's abstract ICanvasRenderingContext,
    // but in browsers it's the real CanvasRenderingContext2D — rasterizeSvg
    // needs the full surface API (drawImage, save, restore).
    const ctx = dt.getContext() as unknown as CanvasRenderingContext2D
    const res = this._resolution
    rasterizeSvg(svgString, ctx, res, res, this._img, (ok) => {
      if (ok) dt.update(false, this._premul)
    })
  }

  dispose() {
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = 0
    }
    this.texture?.dispose()
    this.texture = null as any
  }
}
